import { Grid, Stack, AppBar, Typography, Button, Box } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import CardTitle from './CardTitle';
import { useGetDraftQuery, useUpdateDraftMutation, useGetDraftComparisonsQuery, useLogUserActionMutation } from '../../../api/physicianDashboardSlice';
import { useScrollRestore } from '../../Hooks/useScrollRestore';
import CompleteModal from '../CompleteModal';
import useAnalytics from '../../Hooks/useAnalytics';
import ReviewTabs from '../ReviewPageComponents/ReviewTabs';
import Optimization from '../CardItems/Optimizations/Optimization';
import ReplacementOptimization from '../CardItems/Optimizations/ReplacementOptimization';
import CategoryView from '../ReviewPageComponents/CategoryView';
import { useTheme } from '@mui/system';
import { CelebrationOutlined } from '@mui/icons-material';
import InvalidToken from '../InvalidToken';
import SubmitReviewButton from '../SubmitReviewButton';
import PeerDiscoveryGraph from '../PeerDiscovery/PeerDiscoveryGraph';
import LoadingIndicator from '../../LoadingIndicator';
import ItemDetails from '../ItemDetails/ItemDetails';
import DraftInsightsList from '../PeerDiscovery/Insights/DraftInsightsList';
import { useSearchParams } from 'react-router-dom';
import DraftNotesDrawer from './DraftNotesDrawer';

function CardReview() {
    const { save: saveScroll, restore: restoreScroll } = useScrollRestore();
    const { draftId } = useParams();
    const analytics = useAnalytics();
    const [searchParams] = useSearchParams();
    const currentTab = searchParams.get('current_tab');
    const theme = useTheme();
    const [isShowingCompleteModal, setIsShowingCompleteModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [isItemDetailsOpen, setIsItemDetailsOpen] = useState(false);
    const [isShowingNotes, setIsShowingNotes] = useState(false);
    const [oldSavings, setOldSavings] = useState(0);
    const [currentSavings, setCurrentSavings] = useState(0);
    const [selectedTab, setSelectedTab] = useState(currentTab || 'optimizations');
    const [logUserAction] = useLogUserActionMutation();

    useEffect(() => {
        analytics.track('Page Loaded', {
            sector: 'Physician Dashboard',
            name: 'Single Card View',
            data: {
                cardId: draftId
            }
        });
        window.scrollTo(0, 0);
    }, []);

    const {
        data: draft = {},
        isLoading: draftLoading,
        isSuccess: draftLoaded,
        error
    } = useGetDraftQuery(draftId);
    
    // Fetch peer comparison data
    const { 
        data: analysisData, 
        isLoading: isLoadingAnalysis,
        error: analysisError
    } = useGetDraftComparisonsQuery(
        { draftId }
    );

    // Set defaultCategory to the first category alphabetically
    const defaultCategory = useMemo(() => {
        if (draft && draft.items && Object.keys(draft.items).length > 0) {
            return Object.keys(draft.items).sort()[0];
        }
        return null;
    }, [draft]);

    const optimizations = useMemo(() => {
        var opts = [];
        if (draftLoaded) {
            Object.keys(draft.recommendations).forEach((category) => {
                draft.recommendations[category].forEach((recommendation) => {
                    // Compliance recommendations are shown elsewhere in the dashboard.
                    if (recommendation.type !== 'compliance') {
                        opts.push(recommendation);
                    }
                });
            });
        }

        if (opts.length === 0 && draftLoaded) {
            setSelectedTab('full_card');  // If there are no optimizations once the draft is loaded, default to the All Card Items tab.
        }

        return opts.sort((a, b) => {
            return Math.abs(b['recommendation_value']) - Math.abs(a['recommendation_value']);
        });
    }, [draft.recommendations]);

    const [
        submitChanges,
        { isLoading: isSubmittingChanges },
    ] = useUpdateDraftMutation();

    const handleSubmitChanges = async () => {
        analytics.track('Button Clicked', {
            label: 'Review Complete - Submit Changes',
            sector: 'Physician Dashboard',
            location: 'Single Card View'
        });
        await submitChanges({
            draftId: draft.id,
            status: 'submitted',
        });
        logUserAction({ action: 'submitted_draft', data: { draftId: draft.id, location: 'card_review' } });
        setIsShowingCompleteModal(true);
    }

    function updateCardSavings(newSavings){
        setOldSavings(currentSavings);
        setCurrentSavings(newSavings);
    }

    function openItemView(item) {
        saveScroll();
        setEditingItem(item);
        setIsItemDetailsOpen(true);
    }

    function closeItemView() {
        setIsItemDetailsOpen(false);
        restoreScroll();
    }

    if(error){
        return(
            <InvalidToken />
        )
    }

    return (
        <Grid container sx={{ backgroundColor: '#EDEDED' }}>
            <Grid item md={3} />
            <Grid item xs={12} md={6}>
                { draftLoaded &&
                    <CardTitle 
                        draft={draft} 
                        isLoading={draftLoading} 
                        oldSavings={oldSavings} 
                        currentSavings={currentSavings} 
                        updateCardSavings={updateCardSavings}
                    /> 
                }
                <Grid item xs={12}>
                    <ReviewTabs
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        variant='card_review'
                        logUserAction={logUserAction}
                    />
                </Grid>
                { draftLoaded &&
                    <Stack sx={{ backgroundColor: '#EDEDED' }}>
                        { selectedTab === 'analysis' &&
                            <Stack spacing={2} py={3} px={1}>
                                { isLoadingAnalysis && <LoadingIndicator /> }
                                { !isLoadingAnalysis && !analysisError && analysisData?.cardComparisons &&
                                    <PeerDiscoveryGraph analysisData={analysisData} cardId={draft.card.id} draftId={draftId} />
                                }
                                <DraftInsightsList draftId={draft.id} insights={draft.insights} setSelectedTab={setSelectedTab} />
                            </Stack>
                        }
                        { selectedTab === 'optimizations' && optimizations.length > 0 &&
                            <Stack spacing={2} py={3} px={1.5}>
                                { optimizations.map((optimization) => (
                                    optimization.type === 'swap' ? (
                                        <ReplacementOptimization
                                            key={optimization.id}
                                            recommendation={optimization}
                                            draft={draft}
                                        />
                                    ) : (
                                        <Optimization 
                                            key={optimization.id} 
                                            recommendation={optimization}
                                            draft={draft} 
                                            propItem={
                                                draft.items[optimization.item.category] !== undefined ? 
                                                    draft.items[optimization.item.category].filter((item) => item.id === optimization.item.id)[0] :
                                                    null
                                            }
                                            openItemView={openItemView}
                                            closeItemView={closeItemView}
                                        />
                                    )
                                ))}
                            </Stack>
                        }
                        { selectedTab === 'optimizations' && optimizations.length === 0 &&
                            <Stack spacing={2} pt={4} px={1.5} alignItems='center' sx={{ color: `${theme.palette.subtext.main}` }}>
                                <CelebrationOutlined fontSize='large' />
                                <Typography variant='heavy' sx={{ fontSize: '16px' }}>No Optimizations Here!</Typography>
                            </Stack>
                        }
                        { selectedTab === 'full_card' && 
                            <>
                                <CategoryView
                                    physicianDraft={draft}    
                                    isLoading={draftLoading}
                                    variant='physician_draft'       
                                    seeItemDetails={openItemView}
                                    defaultCategory={defaultCategory}
                                />
                            </>                        
                        }  
                        <AppBar position='sticky' elevation={10} sx={{ marginTop: 5, paddingLeft: 3, paddingRight: 3, bottom: 16, backgroundColor: '#FFFFFF0F', alignSelf: 'center', boxShadow: 0 }}>
                            <Box display="flex" flexDirection="row" alignItems="center" sx={{ gap: 2, width: '100%' }}>
                                <Box sx={{ flex: 1 }}>
                                    <Button variant='orangeContained' fullWidth onClick={() => { setIsShowingNotes(true); }}>
                                        Leave notes
                                    </Button>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <SubmitReviewButton fullWidth onClick={handleSubmitChanges} loading={isSubmittingChanges} />
                                </Box>
                            </Box>
                        </AppBar>
                    </Stack>
                }
                <ItemDetails
                    item={editingItem}
                    draft={draft}
                    open={isItemDetailsOpen}
                    close={closeItemView}
                    variant='card_review'
                />
                <DraftNotesDrawer 
                    open = {isShowingNotes}
                    onClose = {() => setIsShowingNotes(false)}
                    draft={draft}
                    submitChanges={submitChanges}
                    logUserAction={logUserAction}
                />
            </Grid>
            <Grid item md={3} />
            { draftLoaded &&
                <CompleteModal 
                    open={isShowingCompleteModal}
                    setOpen={setIsShowingCompleteModal}
                    physician={draft.card.doctor}
                    campaignId={draft.campaign_id}
                    variant='standard'
                />
            }
        </Grid>
    );
}

export default CardReview;
